import { Button, Flex, Form, Input, Layout, Tabs, Typography } from 'antd';
import styled from 'styled-components';

import { EditOutlined } from '@ant-design/icons';

interface WrapperLayoutProps {
  $isDesktop: boolean;
}

export const WrapperLayout = styled(Layout)<WrapperLayoutProps>`
  height: calc(var(--content-height) - var(--header-height));
  padding-bottom: ${({ $isDesktop }) => ($isDesktop ? 0 : 48)}px;
  ${({ $isDesktop }) =>
    !$isDesktop &&
    `
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - var(--header-height));
  `}
`;

export const PageContent = styled(Flex).attrs({
  flex: 1,
  vertical: true,
})`
  overflow-y: auto;
  overflow-x: hidden;
`;

interface TabsStyledProps {
  $isDesktop: boolean;
  $isDisabled?: boolean;
}

export const TabsStyled = styled(Tabs)<TabsStyledProps>`
  .ant-tabs-nav::before {
    border-color: ${({ theme }) => theme.colorSplit};
  }
  .ant-tabs-nav-wrap {
    padding: 0 ${({ $isDesktop }) => ($isDesktop ? '20px' : 0)};
  }
  .ant-tabs-nav {
    margin-bottom: 0;
    border-color: ${({ theme }) => theme.colorSplit};
  }
  ${({ $isDisabled, theme }) =>
    $isDisabled &&
    `
  .ant-tabs-ink-bar {
    background-color: transparent;
  }
  .ant-tabs-tab-btn {
    color: ${theme.colorTextDisabled} !important;
    font-weight: 400 !important;
    text-shadow: none !important;
  }
  `}
`;

interface TitleStyledProps {
  $entityName?: string;
  $groupName?: string;
  $deviceName?: string;
  $siteName?: string;
}

export const TitleStyled = styled(Typography.Title).attrs<TitleStyledProps>(
  ({ $entityName, $groupName, $deviceName, $siteName }) => {
    const name = $entityName || $groupName || $deviceName || $siteName || '';
    return {
      level: 3,
      ellipsis: { tooltip: name, rows: 1 },
    };
  },
)``;

export const EditIcon = styled(Button).attrs({
  type: 'link',
  icon: <EditOutlined />,
})`
  color: ${({ theme }) => theme.colorPrimary} !important;
  font-size: 24px;
`;

export const TitleWrapper = styled(Flex).attrs({
  flex: 1,
  align: 'center',
})`
  width: 100%;
  margin-top: 8px;
`;

export const ButtonsWrapper = styled(Flex).attrs({
  align: 'end',
  justify: 'space-between',
  gap: 12,
})`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 111px;
  padding: 16px 20px;
  z-index: 10;
  background: linear-gradient(180deg, rgba(244, 244, 244, 0) 0%, ${({ theme }) => theme.colorBgLayout} 56%);
`;

interface TabsContentWrapperProps {
  $isDesktop: boolean;
  $withoutGap?: boolean;
}

export const TabsContentWrapper = styled.div<TabsContentWrapperProps>`
  display: grid;
  row-gap: ${({ $withoutGap }) => ($withoutGap ? 0 : '20px')};
  column-gap: ${({ $withoutGap }) => ($withoutGap ? 0 : '24px')};
  width: 100%;
  margin: 0 auto;
  grid-template-columns:
    1fr
    ${({ $isDesktop }) =>
      $isDesktop &&
      `
      grid-auto-flow: row;
      grid-template-columns: repeat(2, 1fr);
    `};
  .ant-form-item {
    width: 100%;
    height: auto;
  }
  .ant-form-item-row {
    display: flex;
    flex-flow: column;
  }
`;

interface ContentWrapperProps {
  $isDesktop: boolean;
  $withoutGap?: boolean;
  $areMobileButtonsVisible?: boolean;
  $fullHeight?: boolean;
}

export const ContentWrapper = styled.div<ContentWrapperProps>`
  display: block;
  > * + * {
    margin-top: ${({ $withoutGap }) => ($withoutGap ? 0 : '20px')};
  }
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 16px;
  ${({ $areMobileButtonsVisible, $isDesktop, $fullHeight }) => `
    max-height: ${
      $fullHeight
        ? '100%'
        : `calc(
      100dvh - var(--header-height) - var(--tab-height) -
        ${$isDesktop || !$areMobileButtonsVisible ? '92px' : '130px'}
    )`
    };
    padding-bottom: ${$areMobileButtonsVisible ? '0px' : '40px'};
  `}
  padding-bottom: ${({ $isDesktop }) => ($isDesktop ? '20px' : '111px')};
`;

export const TabPageContentWrapper = styled.div<{ $isDesktop: boolean }>`
  display: grid;
  row-gap: 20px;
  column-gap: 24px;
  width: 100%;
  margin: 0 auto;
  ${({ $isDesktop }) => $isDesktop && 'grid-template-columns: repeat(2, 1fr)'}
`;

export const FlexContentWrapper = styled(Flex).attrs({
  flex: 1,
})<{ $isDesktop: boolean }>`
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 16px;
`;

export const StyledNameInput = styled(Input).attrs({
  showCount: true,
  maxLength: 40,
})`
  max-width: 340px;
`;

export const SaveButton = styled(Button).attrs({
  type: 'primary',
})`
  margin-left: 8px;
`;

interface FormItemProps {
  $isDesktop?: boolean;
}

export const FormItem = styled(Form.Item)<FormItemProps>`
  margin-bottom: 0;

  .ant-form-item-row {
    display: flex;
    flex-flow: column;
  }

  .ant-form-item-control {
    flex: 1;
    max-width: 100%;
    width: 100%;
  }

  ${({ $isDesktop }) => !$isDesktop && 'grid-column: span 2;'}
`;

export const FieldDescriptionText = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextDescription};
  display: block;
  margin-top: 4px;
`;
